import { useState, useEffect } from "react";
import { fetchYtdModuleCompletion } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the YTD Module Completion modal.
 *
 * Fires once on mount. Returns a full-year monthly breakdown scoped to the
 * current calendar year. Future months carry completed/cumCompleted: null.
 *
 * Cancels the in-flight request automatically if the component unmounts before
 * the response arrives (AbortController cleanup).
 *
 * Cache TTL: 5 minutes - YTD data updates at most once per day but the modal
 * may be opened repeatedly within a session.
 *
 * @returns {{
 *   metrics: {
 *     ytdCompleted:   number,
 *     ytdTarget:      number,
 *     ytdPct:         number,
 *     deltaVsTarget:  number,
 *     monthlyAvg:     number,
 *     bestMonth:      string | null,
 *     bestMonthCount: number | null,
 *     missedMonths:   number,
 *   },
 *   months: Array<{
 *     month:        string,
 *     target:       number,
 *     completed:    number | null,
 *     cumTarget:    number,
 *     cumCompleted: number | null,
 *   }>,
 *   loading: boolean,
 *   error:   string | null,
 * }}
 */

let _cache = null;
let _cacheTime = 0;
const CACHE_TTL_MS = 5 * 60_000; // 5 minutes

const METRICS_DEFAULT = {
  ytdCompleted: 0,
  ytdTarget: 0,
  ytdPct: 0,
  deltaVsTarget: 0,
  monthlyAvg: 0,
  bestMonth: null,
  bestMonthCount: null,
  missedMonths: 0,
};

const useYtdModuleCompletionData = () => {
  const [metrics, setMetrics] = useState(_cache?.metrics ?? METRICS_DEFAULT);
  const [months, setMonths] = useState(_cache?.months ?? []);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const isStale = !_cache || Date.now() - _cacheTime > CACHE_TTL_MS;
    if (!isStale) return; // already fresh - skip fetch

    const load = async () => {
      try {
        const data = await fetchYtdModuleCompletion(controller.signal);
        _cache = data;
        _cacheTime = Date.now();
        setMetrics(data.metrics);
        setMonths(data.months);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message ?? "Failed to load YTD module completion data.");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, []);

  return { metrics, months, loading, error };
};

export default useYtdModuleCompletionData;
